import React, { useEffect, useMemo, useContext, useCallback, useState, useRef } from 'react';
import { Typography, RadioGroup, DropdownTypeaheadItem, CircularProgress } from '@ellucian/react-design-system/core';
import {
    setDefaultValue,
    setTouched,
    setError,
    setForceShowErrors,
    FormRadio,
    FormCheckbox,
    FormCheckboxGroup,
    FormDropdownTypeahead,
    FormTextField,
    FormDatePicker,
    FormDateRangePicker,
} from '@nsr/react-form';
import { useIntl, FormattedMessage } from 'react-intl';
import { CONSTANTS } from '../Helpers/Configuration';
import { validateEmail as sharedValidateEmail } from '@nsr/applicant-apply-shared/src/helpers/emailValidators';
import isUrlValid from '../Helpers/UrlValidation';
import { isProspectEmailLocked, isUaePassControlledFieldLocked } from '../Helpers/TemplateHelpers';
import useApplicationForm from './useApplicationForm';
import moment from 'moment';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import { useApi } from '../ApiContext';
import { batch, useDispatch, useSelector } from 'react-redux';
import { fetchValuesOnSearch } from '../Helpers/dataAccessFetch';
import FormFilteredSearch from './FilteredSearch/FormFilteredSearch';
import { selectCurriculumValues, selectCurriculumLoading, setCode } from '../ApplicationFormLayout/redux/slices/applicationSlice';
import { createSelector } from '@reduxjs/toolkit';
import get from 'lodash/get';
import identity from 'lodash/identity';
import toPath from 'lodash/toPath';
import { getCodeFieldInfoForQuestion } from '@nsr/applicant-apply-shared/src/helpers/common';
import { fetchCurriculum } from './redux/actions/applicationActions';
import { setValue } from '@nsr/react-form/dist/components/redux/slices/formSlice';
import { navigateQuestions } from '../Helpers/ApplicationHelpers';
import DOMPurify from 'dompurify';
import { getCrossFieldExcludedValues, getSameRepetitionExcludedValues } from '../Helpers/repeatingFieldHelper';
import FormPhoneField from '../ReduxFormComponents/FormPhoneField';
import FormSearch from '../ReduxFormComponents/FormSearch';
import FormUploadQuestion from '../ReduxFormComponents/FormUploadQuestion';
import { isUnderMinimumAge } from '../Helpers/dateValidationHelpers';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';

const selectApplicationId = state => state.application?.id;
const selectCodes = state => state.application?.codes;
const selectProspectEmail = state => state.application?.prospect?.email;
const selectFormValues = state => state.form?.values;
const codeInputPropertyName = 'data-code';
const defaultEndYear = moment().add(50, 'years').year();
const defaultStartYear = moment().subtract(30, 'years').year();

const Question = props => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const { template, adminTenant, adminApi, previewMode, importAdminMode } = useApplicationForm();
    const contextApi = useApi();
    const api = adminApi || contextApi;
    const applicationId = useSelector(selectApplicationId);
    const {
        question,
        prefix = false,
        isConditionallyRequired,
        pageId
    } = props;
    const name = prefix ? `${prefix}.${question.id}` : question.id;
    const { defaultValue, defaultSelectionValues, defaultSelectionsRequired } = question;
    const invalidFormat = intl.formatMessage({ id: 'invalidFormatErrorTextId' });
    const maxSearchOptionsWarning = intl.formatMessage({ id: 'maxSearchOptionsWarningTextId' });
    const invalidEmailFormat = intl.formatMessage({ id: 'invalidEmailFormatTextId' });
    const { globalLocale } = useContext(GlobalLocaleContext);
    const tenant = window.PRELOADED_STATE?.globalSettings || adminTenant;
    const rawDateFormat = tenant?.settingMessages?.languages[globalLocale]?.dateFormat || CONSTANTS.defaultDateFormat;
    const formatIndex = CONSTANTS.dateFormats.findIndex(f => f.toLowerCase() === rawDateFormat.toLowerCase());
    const dateFormat = formatIndex >= 0 ? CONSTANTS.dateFormats[formatIndex] : CONSTANTS.defaultDateFormat;           // lowercase — for DatePicker (date-fns)
    const dateFormatDisplay = formatIndex >= 0 ? CONSTANTS.dateFormatsDisplay[formatIndex] : CONSTANTS.defaultDateFormatDisplay; // uppercase — for helperText (user display)
    const { supportedGraphqlModels } = tenant;
    const codeFieldInfo = getCodeFieldInfoForQuestion(question);
    const codeField = codeFieldInfo?.codeField;
    const selectCode = useMemo(() => createSelector(
        selectCodes,
        (codes) => get(codes, name)  // Use name from closure directly
    ), [name]);
    const selectedCode = useSelector(selectCode);
    const curriculumValues = useSelector(selectCurriculumValues);
    const curriculumLoading = useSelector(selectCurriculumLoading);
    const prospectEmail = useSelector(selectProspectEmail);
    const formValues = useSelector(selectFormValues);
    const isUaePassAuthenticated = window.PRELOADED_STATE?.isUaePassAuthenticated === true;
    const uaePassSuppliedFields = window.PRELOADED_STATE?.uaePassSuppliedFields || {};
    const snackbar = useSnackbar();
    const lastCurriculumValidRef = useRef(curriculumValues?.valid);

    // Show snackbar when curriculum rules validation fails
    useEffect(() => {
        // Only show snackbar if validation just became invalid (was valid before, now invalid)
        // We only want to show the error snackbar after the curriculum data has finished loading and we have a definitive
        if (!curriculumLoading &&
            curriculumValues &&
            curriculumValues.valid === false &&
            lastCurriculumValidRef.current !== false) {
            snackbar.error(intl.formatMessage({ id: 'curriculumRulesValidationError' }));
        }
        lastCurriculumValidRef.current = curriculumValues?.valid;
    }, [curriculumValues, curriculumLoading, snackbar, intl]);
    const selectCurrentFieldValue = useMemo(
        () => state => get(state.form?.values, toPath(name)),
        [name]
    );
    const currentFieldValue = useSelector(selectCurrentFieldValue);
    const required = useMemo(() => {
        return question?.required || isConditionallyRequired || false;
    }, [question.required, isConditionallyRequired]);

    const repetition = useMemo(() => {
        // If we are in a repeating section,
        // The repetition index is the third part of the prefix.
        let rep = undefined;
        const parts = prefix?.split?.call(prefix, '.');

        if (parts?.length === 3) {
            try {
                rep = parseInt(parts[2]);
            } catch {
                // Assume no repetition.
            }
        }

        return rep;
    }, [prefix]);
    const selectTouched = useMemo(() => {
        return createSelector(
            state => get(state.form.touched, toPath(name)),
            identity
        );
    }, [name]);
    const touched = useSelector(selectTouched);
    useEffect(() => {
        if (defaultValue !== undefined && question.type !== CONSTANTS.controlTypes.singlechoice) {
            dispatch(setDefaultValue({
                name,
                value: defaultValue,
            }));
        }
    }, [defaultValue, dispatch]);
    useEffect(() => {
        const hasDefaultValue = defaultSelectionValues?.length || (defaultValue !== undefined && question.type !== CONSTANTS.controlTypes.singlechoice);
        // Currently, a hidden question cannot have conditions. That means it cannot be conditionally shown meaning it never can get a value
        // So we just have to validate against a hidden required field that doesn't have a default value and not account for value changes
        if (question.required && question.hidden && !hasDefaultValue) {
            batch(() => {
                dispatch(setError({
                    name: name,
                    value: intl.formatMessage({ id: 'validateRequired' })
                }));
                dispatch(setForceShowErrors({
                    name: pageId,
                    value: true
                }));
            });
        }
    }, [defaultSelectionValues, defaultValue]);

    const isMajorDescription = question.id === CONSTANTS.majorDescriptionSelfServiceQuestionId;
    const curriculumQuestion = CONSTANTS.curriculumCachedQuestions.find(
        cq => (question.eedmDestinationAttribute && (question.eedmDestinationAttribute === cq.applicationAttributePath || question.eedmDestinationAttribute === cq.inquiryAttributePath))
    );
    const {
        majorQuestion,
        programQuestion
    } = useMemo(() => {
        const ret = {};

        if (isMajorDescription && template?.cachedQuestions?.length) {
            const majorPath = template.cachedQuestions.find(
                q => q.key === 'major'
            )?.questionPath;
            const programPath = template.cachedQuestions.find(
                q => q.key === 'program'
            )?.questionPath;

            navigateQuestions(template, (_p, _s, _g, q) => {
                if (majorPath?.endsWith(q.id)) {
                    ret.majorQuestion = q;
                } else if (programPath?.endsWith(q.id)) {
                    ret.programQuestion = q;
                }
            });
        }

        return ret;
    }, [template, isMajorDescription]);
    // Check if this question is a "type" field in a repeating section
    const isRepeatingTypeField = useMemo(() => {
        const attr = question.eedmDestinationAttribute;
        if (!attr || repetition === undefined) {
            return false;
        }
        return CONSTANTS.repeatingTypeFields.includes(attr);
    }, [question.eedmDestinationAttribute, repetition]);

    const selectSectionValues = useMemo(() => {
        if (!isRepeatingTypeField || repetition === undefined) {
            return () => null;
        }
        const parts = prefix?.split?.('.') || [];
        if (parts.length !== 3) {
            return () => null;
        }
        const [sectionPageId, sectionId] = parts;
        return state => get(state.form?.values, [sectionPageId, sectionId]);
    }, [isRepeatingTypeField, repetition, prefix]);
    const sectionValues = useSelector(selectSectionValues);

    // Memoized selector scoped to the current page only — feeds getCrossFieldExcludedValues.
    // Returns a stable () => null for questions without cross-field constraints,
    // avoiding the broad form-values subscription that caused asterisk flicker.
    const selectPageValues = useMemo(() => {
        const attr = question.eedmDestinationAttribute;
        const hasRelatedAttr = !!(attr && CONSTANTS.relatedTypeFieldAttributes?.[attr]);
        if (!hasRelatedAttr || !prefix) return () => null;
        const pageId = prefix.split('.')[0];
        return state => state.form?.values?.[pageId] ?? null;
    }, [question.eedmDestinationAttribute, prefix]);
    const pageLevelValues = useSelector(selectPageValues);

    const selectedTypesInOtherRepetitions = useMemo(() => {
        const parts = prefix?.split?.('.') || [];
        const [pageId, sectionId] = parts;
        const wrappedSectionValues = (pageId && sectionId && Array.isArray(sectionValues))
            ? { [pageId]: { [sectionId]: sectionValues } }
            : null;
        const pageValues = (pageId && pageLevelValues != null)
            ? { [pageId]: pageLevelValues }
            : null;
        return [
            ...getSameRepetitionExcludedValues({ isRepeatingTypeField, repetition, prefix, formValues: wrappedSectionValues, questionId: question.id }),
            ...getCrossFieldExcludedValues({ question, prefix, formValues: pageValues, template })
        ];
    }, [isRepeatingTypeField, repetition, prefix, sectionValues, pageLevelValues, question.id, question.eedmDestinationAttribute, template]);
    const possibleValues = useMemo(() => {
        const questionCurriculumValues = curriculumQuestion &&
            curriculumValues?.[curriculumQuestion.keyPlural];
        let pvs = question.possibleValues?.values;

        if (questionCurriculumValues?.length) {
            pvs = question.possibleValues?.values?.filter(
                v => questionCurriculumValues.includes(v.code)
            );
        } else if (isMajorDescription) {
            if (Array.isArray(curriculumValues?.majorDescriptions)) {
                pvs = [...curriculumValues.majorDescriptions];
                pvs.sort((a, b) => {
                    if (a.label < b.label) {
                        return -1;
                    } else if (a.label > b.label) {
                        return 1;
                    } else {
                        return 0;
                    }
                });
            } else {
                pvs = [];
            }
        }
        // Filter out values excluded by same-section dedup or cross-field constraints
        if (selectedTypesInOtherRepetitions.length > 0 && pvs) {
            pvs = pvs.filter(v => !selectedTypesInOtherRepetitions.includes(v.value));
        }
        return pvs;
    }, [question.possibleValues?.values, curriculumValues, curriculumQuestion, isMajorDescription, selectedTypesInOtherRepetitions]);

    // Auto-clear the current value if it has become excluded (e.g. the same value was just selected in a related field)
    useEffect(() => {
        if (!currentFieldValue || !selectedTypesInOtherRepetitions.includes(currentFieldValue)) return;
        batch(() => {
            dispatch(setValue({ name, value: undefined }));
            dispatch(setCode({ path: name, code: null }));
        });
    }, [selectedTypesInOtherRepetitions, currentFieldValue, name, dispatch]);

    useEffect(() => {
        // If the question is not answered and has a default value, set the default value
        if (!selectedCode && !touched && defaultSelectionValues?.length && defaultSelectionValues.length > 0) {
            const code = question?.possibleValues?.values?.find(v => v.value === defaultSelectionValues[repetition ? repetition : 0])?.code;
            dispatch(setCode({
                path: name,
                code: code || null
            }));
            dispatch(setDefaultValue({
                name,
                value: defaultSelectionValues[repetition ? repetition : 0],
            }));
            if (code) {
                refreshCurriculum(code);
            }
        }
    }, [defaultSelectionValues, dispatch, refreshCurriculum]);

    // Check if this question is the primary prospect email that should be locked
    const isLockedProspectEmail = useMemo(() => {
        // Only apply the lock in non-preview, non-import-admin mode
        if (previewMode || importAdminMode) {
            return false;
        }
        return isProspectEmailLocked(question, template, prospectEmail, currentFieldValue);
    }, [question, template, previewMode, importAdminMode, prospectEmail, currentFieldValue]);

    const isLockedUaePassField = useMemo(() => {
        if (previewMode || importAdminMode) {
            return false;
        }
        if (window.PRELOADED_STATE?.globalSettings?.uaepassSettings?.lockUaePassFields === false) {
            return false;
        }
        return isUaePassControlledFieldLocked(question, isUaePassAuthenticated, uaePassSuppliedFields, template, formValues, prefix);
    }, [question, isUaePassAuthenticated, uaePassSuppliedFields, previewMode, importAdminMode, template, formValues, prefix]);

    const disabled = useMemo(() => {
        let disabled = false;
        if (defaultSelectionsRequired?.length && defaultSelectionsRequired.length > 0) {
            if (repetition >= 0 && defaultSelectionsRequired[repetition]) {
                disabled = defaultSelectionsRequired[repetition] ? defaultSelectionsRequired[repetition] : false;
            }
            else if (!repetition && defaultSelectionsRequired[0]) {
                disabled = defaultSelectionsRequired[0];
            }
        }
        // Also disable if this is the locked prospect email field
        return disabled || isLockedProspectEmail || isLockedUaePassField;
    }, [defaultSelectionsRequired, isLockedProspectEmail, isLockedUaePassField]);

    useEffect(() => {
        if (importAdminMode && !touched && required || isConditionallyRequired) {
            dispatch(setTouched({ name: name, value: true }));
        }
    }, [dispatch, required, importAdminMode, touched, isConditionallyRequired]);
    const validateEmail = (value) => {
        if (value?.length) {
            const result = sharedValidateEmail(value);
            return !result.isValid ? invalidEmailFormat : undefined;
        }
    };
    const validateNumber = (value) => {
        if (value?.length) {
            return isNaN(value) ? invalidFormat : undefined;
        }
    };
    const validateUrl = (value) => {
        if (value?.length) {
            return isUrlValid(value) ? undefined : invalidFormat;
        }
    };
    const validateMin = (question, value) => {
        // Check for a non-empty string shorter than min characters.
        let valid = true;

        if (
            question.minCharacters &&
            value !== null && value !== undefined
        ) {
            const stringValue = value.toString();

            if (
                stringValue && stringValue.length &&
                stringValue.length < question.minCharacters
            ) {
                valid = false;
            }
        }

        return valid ? undefined : intl.formatMessage({ id: 'invalidMinCount' });
    };
    const validateMax = (question, value) => {
        var isValid = false;

        switch (question.inputType) {
            case 'number':
                isValid = value === '' || (value !== null && value !== undefined && !isNaN(value) && value.toString().length <= question.maxCharacters);
                break;
            default:
                isValid = value === '' || (!!value && value?.length <= question.maxCharacters);
                break;
        }

        isValid = (question.required) ? value !== '' && isValid : isValid;

        return (!isValid) ? intl.formatMessage({ id: 'invalidMaxCount' }) : undefined;
    };

    const getRegexCustomErrorMessage = (questionObj) => {
        if (!questionObj?.showRegexCustomError) {
            return '';
        }

        if (questionObj?.regexCustomErrorMessageId) {
            const localizedMessage = intl.formatMessage({
                id: questionObj?.regexCustomErrorMessageId,
                defaultMessage: ''
            });
            if (localizedMessage) {
                return localizedMessage;
            }
        }

        return questionObj?.regexCustomErrorMessage || '';
    };

    const validateRegex = (questionObj, value) => {
        if (value === CONSTANTS.piiMaskedValueRegexError) {
            return intl.formatMessage({ id: 'invalidRegexValue' });
        }
        else if (value === CONSTANTS.piimaskedValue) {
            return undefined;
        }
        var regularExpressionValidation;
        try {
            regularExpressionValidation = new RegExp(questionObj.regexValidationExpression);
        }
        catch {
            // If we are in previewMode meaning this is not a student then show the invalid regular expression error
            // If this is a student then pass the regex test by returning undefined
            return previewMode ? intl.formatMessage({ id: 'invalidRegexExpression' }) : undefined;
        }
        if (value?.length) {
            const isValid = regularExpressionValidation.test(value);
            if (!isValid) {
                const customRegexErrorMessage = getRegexCustomErrorMessage(questionObj);
                if (customRegexErrorMessage) {
                    return customRegexErrorMessage;
                }
                return intl.formatMessage({ id: 'invalidRegexValue' });
            }
        }
    };

    const textValidation = useCallback(value => {
        let errorMessage = undefined;
        if (question.inputType) {
            switch (question.inputType) {
                case 'email':
                    errorMessage = validateEmail(value);
                    break;
                case 'number':
                    errorMessage = validateNumber(value);
                    break;
                case 'url':
                    errorMessage = validateUrl(value);
                    break;
                default:
                    break;
            }
        }
        if (!errorMessage && question.showMinChar && question.minCharacters) {
            errorMessage = validateMin(question, value);
        }
        if (!errorMessage && question.showMaxChar && question.maxCharacters) {
            errorMessage = validateMax(question, value);
        }
        if (!errorMessage && question.showRegexValidation && question.regexValidationExpression) {
            errorMessage = validateRegex(question, value);
        }
        return errorMessage;
    }, [question, invalidEmailFormat, invalidFormat, intl, previewMode]);

    const dateValidation = useCallback(value => {
        let errorMessage = undefined;

        if (value && typeof value.getFullYear === 'function') {
            const yyyy = value.getFullYear();

            if (question.endYear || question.startYear) {
                if (question.startYear && yyyy < question.startYear) {
                    errorMessage = intl.formatMessage({ id: 'invalidDateBeforeStartYear' }, { year: `01/01/${question.startYear}` });
                }
                else if (question.endYear && yyyy > question.endYear) {
                    errorMessage = intl.formatMessage({ id: 'invalidDateAfterEndYear' }, { year: `12/31/${question.endYear}` });
                }
            }

            // Year < 100 = 2-digit year typo (e.g. "24-06-26" → year 24 CE).
            if (!errorMessage && yyyy < 100) {
                errorMessage = intl.formatMessage({ id: 'invalidDateBeforeStartYear' }, { year: `01/01/${question.startYear || defaultStartYear}` });
            }
        }

        // Enforce minimum age by rejecting dates that make the applicant too young.
        if (!errorMessage && value && typeof (value.getFullYear) == 'function' && isUnderMinimumAge(question.minAge, value)) {
            errorMessage = intl.formatMessage({ id: 'invalidDateForMinimumAge' }, { age: question.minAge });
        }

        return errorMessage;
    }, [question, intl]);

    // Re-apply datepicker validation after reload so existing date errors remain visible.
    useEffect(() => {
        if (question.type !== CONSTANTS.controlTypes.datepicker || currentFieldValue === null || currentFieldValue === undefined || currentFieldValue === '') {
            return;
        }

        const valueToValidate = formatDate(currentFieldValue);
        const dateError = dateValidation(valueToValidate);

        batch(() => {
            dispatch(setError({
                name,
                value: dateError
            }));

            if (dateError) {
                dispatch(setForceShowErrors({
                    name: pageId,
                    value: true
                }));
            }
        });
    }, [question.type, currentFieldValue, dateValidation, dispatch, name, pageId]);

    const calculateInitialMonth = useMemo(() => {
        let initialDate;

        if (question.startYear || question.endYear) {
            initialDate = new Date();
            let initialYYYY = initialDate.getFullYear();

            //If startYear is in future, the initialDate is the lowerbound startYear
            if (question.startYear && question.startYear > initialYYYY) {
                initialDate.setFullYear(question.startYear);
            }

            //If endYear is in past, the initialDate is the upperbound endYear
            else if (question.endYear && question.endYear < initialYYYY) {
                initialDate.setFullYear(question.endYear);
            }
        }

        return initialDate;
    }, [question]);

    const makeOptionArray = (options, type) => {
        let optionsArray = [...options];
        optionsArray.forEach(option => {
            if (type !== CONSTANTS.questionPossibleValueTypes.dynamic) {
                if (option.labelId) {
                    option.label = intl.formatMessage({ id: option.labelId });
                }
                else {
                    option.label = option.value;
                }
            }
        });
        return optionsArray;
    };
    // For checkbox and singlechoice, get label based on static/dynamic values
    const getListLabel = (value) => {
        if (question.possibleValues.type === CONSTANTS.questionPossibleValueTypes.dynamic) {
            return value.label;
        }
        return value.labelId ? intl.formatMessage({ id: value.labelId }) : value.value;
    };
    const label = useMemo(() => {
        if (question.labelId) {
            return intl.formatMessage({ id: question.labelId });
        }
        return '';
    }, [intl, question]);

    function formatDate(value) {
        const formattedDate = value ? moment(value).toDate() : null;
        return formattedDate;
    }

    // Rejects non-Date/Invalid Date so unparseable input is never stored.
    const formatDateToStore = useCallback((dateValue) => {
        if (!dateValue || !(dateValue instanceof Date) || isNaN(dateValue.getTime())) {
            return null;
        }
        return moment(dateValue).format('YYYY-MM-DD');
    }, []);

    const logAdornmentClick =
        applicationId && !previewMode &&
        adminApi && question.inputType === 'password';

    const onAdornmentClick = useCallback((_ev, passwordVisible) => {
        if (typeof passwordVisible === 'boolean') {
            if (passwordVisible) {
                adminApi?.applicationForms?.unmaskPii?.call(this, applicationId, label, repetition);
            } else {
                adminApi?.applicationForms?.maskPii?.call(this, applicationId, label, repetition);
            }
        }
    }, [label, repetition, adminApi, applicationId]);

    // Firefox ignores autocomplete="off" on type="password" inputs and always
    // shows a "save password" popup — this is a deliberate browser security policy that
    // cannot be overridden with attributes. The fix for Firefox: use type="text" and
    // manually mask the displayed value with asterisks on blur, restoring the real value
    // on focus. Chrome/other browsers are unaffected and keep type="password" + passwordToggle.
    const isFirefox = /firefox/i.test(navigator.userAgent);
    const [piiFieldFocused, setPiiFieldFocused] = useState(false);

    const handleBlur = useCallback((event) => {
        let escapedValue = DOMPurify.sanitize(event.target.value?.trim());
        if (question.inputType === 'email' && escapedValue) {
            escapedValue = sharedValidateEmail(escapedValue).normalizedEmail || escapedValue;
        }
        dispatch(
            setValue({
                name: name,
                value: escapedValue
            })
        );
    }, [dispatch, name, question]);

    const piiFormatFromStore = useCallback((value) => {
        if (!piiFieldFocused && value) {
            return '*'.repeat(value.length);
        }
        return value || '';
    }, [piiFieldFocused]);

    const handlePiiFieldFocus = useCallback(() => {
        setPiiFieldFocused(true);
    }, []);

    const handlePiiFieldBlur = useCallback((event) => {
        handleBlur(event);
        setPiiFieldFocused(false);
    }, [handleBlur]);

    const refreshCurriculum = useCallback((value) => {
        let changedQuestion = curriculumQuestion?.key;

        if (!changedQuestion && isMajorDescription) {
            changedQuestion = 'majorDescription';
        }

        if (changedQuestion) {
            dispatch(fetchCurriculum({
                changedQuestion,
                changedValue: value,
                api
            }));
        }
    }, [curriculumQuestion, api, isMajorDescription]);

    const handleMajorDescription = useCallback((program, major) => {
        const { cachedQuestions = [] } = template;
        // TODO: should these keys be constants?
        const majorPath = cachedQuestions.find(
            q => q.key === 'major'
        )?.questionPath;
        const programPath = cachedQuestions.find(
            q => q.key === 'program'
        )?.questionPath;

        batch(() => {
            if (majorPath) {
                let value = major;
                if (majorQuestion?.possibleValues?.values?.length) {
                    const id = majorQuestion.possibleValues.values.find(
                        pv => pv.code === major
                    )?.value;

                    if (id) {
                        value = id;
                    }
                }
                dispatch(setValue({
                    name: majorPath,
                    value
                }));
                dispatch(setCode({
                    path: majorPath,
                    code: major
                }));
            }

            if (programPath) {
                let value = program;
                if (programQuestion?.possibleValues?.values?.length) {
                    const id = programQuestion.possibleValues.values.find(
                        pv => pv.code === program
                    )?.value;

                    if (id) {
                        value = id;
                    }
                }
                dispatch(setValue({
                    name: programPath,
                    value
                }));
                dispatch(setCode({
                    path: programPath,
                    code: program
                }));
            }
        });
    }, [majorQuestion, programQuestion, dispatch, template]);

    const recordDropdownCode = useCallback((_e) => {
        const record = possibleValues?.find(v => v.value === _e);
        if (isMajorDescription) {
            const {
                program = null,
                major = null
            } = record || {};

            handleMajorDescription(program, major);
            refreshCurriculum({ program, major });
        } else {
            const code = record?.code;

            dispatch(setCode({
                path: name,
                code: code || null
            }));
            refreshCurriculum(code);
        }
    }, [name, possibleValues, refreshCurriculum, isMajorDescription, handleMajorDescription]);

    const recordCheckboxCode = useCallback(e => {
        const code = e?.target?.getAttribute && e.target.getAttribute(codeInputPropertyName);
        const { checked } = e.target;
        if (code) {
            let newCodes;

            if (checked) {
                newCodes = [
                    ...(selectedCode || []),
                    code
                ];
            } else {
                newCodes = (selectedCode || []).filter(c => c !== code);
            }

            dispatch(setCode({
                code: newCodes,
                path: name
            }));
        }
    }, [selectedCode, name]);

    const checkboxInputProps = useMemo(() => {
        const values = possibleValues || [];
        let inputProps;

        if (values.some(v => v.code)) {
            return values.map(v => ({
                [codeInputPropertyName]: v.code
            }));
        } else {
            inputProps = [];
        }

        return inputProps;
    }, [possibleValues]);

    const onSelectSearchAndSelect = value => {
        if (Array.isArray(possibleValues)) {
            possibleValues.push(value);
        }

        if (value.code) {
            dispatch(setCode({
                path: name,
                code: [
                    ...(selectedCode || []),
                    value.code
                ]
            }));
        }
    };

    const onRemoveSearchAndSelect = value => {
        if (value.code) {
            dispatch(setCode({
                path: name,
                code: (selectedCode || []).filter(c => c !== value.code)
            }));
        }
    };

    const filteredSearchChange = selectedRecord => {
        if (selectedRecord) {
            possibleValues?.push(selectedRecord);
        }

        if (codeFieldInfo) {
            const code = selectedRecord?.[codeFieldInfo.codeFieldPath];

            dispatch(setCode({
                path: name,
                code: code || null
            }));

            refreshCurriculum(code);
        }
    };

    return (
        <div> {!question?.hidden &&
            <div>
                {question.type === CONSTANTS.controlTypes.text &&
                    <FormTextField
                        fullWidth={true}
                        name={name}
                        label={label}
                        required={required}
                        disabled={disabled && !isLockedUaePassField}
                        inputProps={{ readOnly: isLockedUaePassField }}
                        requiredErrorTextId="requiredFieldErrorTextId"
                        maxCharacters={question.showMaxChar ? question.maxCharacters : CONSTANTS.maxQuestionTextLength}
                        helperText={question.showHelp && question.helpId ? intl.formatMessage({ id: question.helpId }) : undefined}
                        type={question.inputType === 'password' && !isFirefox ? 'password' : ''}
                        autoComplete={question.inputType === 'password' ? 'new-password' : 'off'}
                        validate={textValidation}
                        passwordToggle={question.inputType === 'password' && !isFirefox}
                        onAdornmentClick={logAdornmentClick ? onAdornmentClick : undefined}
                        formatFromStore={question.inputType === 'password' && isFirefox ? piiFormatFromStore : undefined}
                        onFocus={question.inputType === 'password' && isFirefox ? handlePiiFieldFocus : undefined}
                        onBlur={question.inputType === 'password' && isFirefox ? handlePiiFieldBlur : handleBlur}
                        debounceTime={0}
                    />
                }
                {question.type === CONSTANTS.controlTypes.textarea &&
                    <FormTextField
                        fullWidth={true}
                        name={name}
                        label={label}
                        required={required}
                        requiredErrorTextId="requiredFieldErrorTextId"
                        maxCharacters={question.showMaxChar ? question.maxCharacters : CONSTANTS.maxQuestionTextLength}
                        multiline
                        helperText={question.showHelp && question.helpId ? intl.formatMessage({ id: question.helpId }) : undefined}
                        type={question.inputType === 'password' ? question.inputType : ''}
                        rows={question.showDefaultRows && !question.showMaxRows ? question.defaultRows : undefined}
                        maxRows={question.showMaxRows ? question.maxRows : undefined}
                        minRows={question.showMaxRows ? question.defaultRows : undefined}
                        validate={textValidation}
                        passwordToggle={question.inputType === 'password'}
                        onAdornmentClick={logAdornmentClick ? onAdornmentClick : undefined}
                        onBlur={handleBlur}
                        debounceTime={0}
                    />
                }
                {question.type === CONSTANTS.controlTypes.radiogroup &&
                    <>
                        <fieldset>
                            <legend>
                                <Typography>{label}</Typography>
                            </legend>
                            <RadioGroup>
                                {possibleValues.map((value, valueIndex) => (
                                    <FormRadio
                                        key={valueIndex}
                                        label={value.labelId ? intl.formatMessage({ id: value.labelId }) : value.value}
                                        name={name}
                                        value={value.value}
                                        code={value.code}
                                    />
                                ))}
                            </RadioGroup>
                            {question.showHelp && question.helpId &&
                                <Typography variant="body3">{intl.formatMessage({ id: question.helpId })}</Typography>
                            }
                        </fieldset>
                    </>
                }
                {question.type === CONSTANTS.controlTypes.checkbox &&
                    <>
                        <fieldset>
                            <legend>
                                <Typography>{label}</Typography>
                            </legend>
                            <FormCheckboxGroup
                                name={name}
                                helperText={question.showHelp && question.helpId ? intl.formatMessage({ id: question.helpId }) : null}
                                required={required}
                                requiredErrorTextId="requiredFieldErrorTextId"
                            >
                                {possibleValues.map((value, valueIndex) => (
                                    <div key={valueIndex}>
                                        <FormCheckbox
                                            label={getListLabel(value)}
                                            name={name}
                                            value={value.value}
                                            inputProps={checkboxInputProps[valueIndex]}
                                            onChange={recordCheckboxCode}
                                        />
                                    </div>
                                ))}
                            </FormCheckboxGroup>
                        </fieldset>
                    </>
                }
                {question.type === CONSTANTS.controlTypes.singlechoice &&
                    <FormDropdownTypeahead
                        fullWidth={true}
                        name={name}
                        label={label}
                        required={required}
                        onChange={recordDropdownCode}
                        requiredErrorTextId="requiredFieldErrorTextId"
                        helperText={question.showHelp && question.helpId ? intl.formatMessage({ id: question.helpId }) : null}
                    >
                        <DropdownTypeaheadItem
                            label=""
                            value=""
                        />
                        {(curriculumQuestion || isMajorDescription) && curriculumQuestion?.key !== 'term' && curriculumLoading ? (
                            <DropdownTypeaheadItem
                                label={intl.formatMessage({ id: 'loading' })}
                                value="loading"
                                LeftIconComponent={<CircularProgress size={20} />}
                            />
                        ) : possibleValues.map(value => {
                            if ((disabled && repetition >= 0 && value.value !== defaultSelectionValues[repetition]) || (disabled && !repetition && value.value !== defaultSelectionValues[0])) {
                                return (
                                    <>
                                    </>
                                );
                            }
                            else {
                                return (
                                    <DropdownTypeaheadItem
                                        key={value.value}
                                        label={getListLabel(value)}
                                        value={value.value}
                                        code={value.code}
                                    />
                                );
                            }
                        })}
                    </FormDropdownTypeahead>
                }
                {question.type === CONSTANTS.controlTypes.datepicker &&
                    <FormDatePicker
                        format={dateFormat || CONSTANTS.formats.dateFormatStrings[0]}
                        fullWidth={true}
                        name={name}
                        label={label}
                        initialMonth={calculateInitialMonth}
                        required={required}
                        requiredErrorTextId="requiredFieldErrorTextId"
                        placeholder={question.placeholder ? intl.formatMessage({ id: question.placeholder }) : null}
                        helperText={<FormattedMessage id="datepickerHelperText" values={{ dateFormat: dateFormatDisplay }} />}
                        formatFromStore={formatDate}
                        formatToStore={formatDateToStore}
                        withMonthYearSelect={true}
                        validate={dateValidation}
                        // Path 8 DatePicker uses minDate/maxDate directly; keep the configured years inclusive.
                        minDate={new Date((question.startYear || defaultStartYear), 0, 1)}
                        maxDate={new Date((question.endYear || defaultEndYear), 11, 31)}

                    />
                }
                {question.type === CONSTANTS.controlTypes.daterange &&
                    <FormDateRangePicker
                        fullWidth={true}
                        name={name}
                        format={dateFormat || CONSTANTS.formats.dateFormatStrings[0]}
                        maxDate={question.maxDate ? moment(question.maxDate).toDate() : undefined}
                        minDate={question.minDate ? moment(question.minDate).toDate() : new Date(`01/01/${(new Date()).getFullYear() - CONSTANTS.dateRangePickerMinDateYearRange}`)}
                        required={required}
                        requiredErrorTextId="requiredFieldErrorTextId"
                        startInputLabel={label ? label : null}
                        endInputLabel={question.endLabelId ? intl.formatMessage({ id: question.endLabelId }) : null}
                        helperText={question.showHelp && question.helpId ? intl.formatMessage({ id: question.helpId }) : null}
                    />
                }
                {question.type === CONSTANTS.controlTypes.phone &&
                    <FormPhoneField
                        countryLabel={intl.formatMessage({ id: 'phoneCountryLabel' })}
                        countryOptionsOrder={question.countryList}
                        defaultCountry={question.defaultCountry}
                        helperText={question.showHelp && question.helpId ? intl.formatMessage({ id: question.helpId }) : null}
                        invalidFormatText={invalidFormat}
                        label={label}
                        name={name}
                        required={required}
                        requiredErrorTextId="requiredFieldErrorTextId"
                        country={question.defaultCountry}
                    />
                }
                {question.type === CONSTANTS.controlTypes.searchandselect &&
                    <FormSearch
                        name={name}
                        required={required}
                        requiredErrorTextId="requiredFieldErrorTextId"
                        label={label}
                        helperText={question.showHelp && question.helpId ? intl.formatMessage({ id: question.helpId }) : null}
                        fullWidth={true}
                        options={makeOptionArray(possibleValues, question.possibleValues.type)}
                        maxSelections={question.maxSelections}
                        searchDelay={CONSTANTS.searchSelectDelay}
                        dynamicValues={question.possibleValues.type === CONSTANTS.questionPossibleValueTypes.dynamic}
                        onSearch={
                            question.possibleValues.type === CONSTANTS.questionPossibleValueTypes.dynamic ?
                                (searchText) => fetchValuesOnSearch(api.dataAccess, question, searchText, supportedGraphqlModels) : undefined
                        }
                        onSelect={onSelectSearchAndSelect}
                        onDeleteItem={onRemoveSearchAndSelect}
                        maxSearchOptionsWarningText={maxSearchOptionsWarning}
                    />
                }
                {question.type === CONSTANTS.controlTypes.filteredsearch &&
                    <FormFilteredSearch
                        filteredSearch={question?.filteredSearch ?? {}}
                        possibleValues={question?.possibleValues ?? []}
                        name={name}
                        required={required}
                        requiredErrorTextId="requiredFieldErrorTextId"
                        label={label}
                        helperText={question.showHelp && question.helpId ? intl.formatMessage({ id: question.helpId }) : null}
                        fullWidth={true}
                        onChange={filteredSearchChange}
                        codeFieldInfo={codeField}
                    >
                    </FormFilteredSearch>
                }
                {question.type === CONSTANTS.controlTypes.upload &&
                    <FormUploadQuestion
                        name={name}
                        label={label}
                        required={required}
                        disabled={disabled}
                        question={question}
                        previewMode={previewMode}
                    />
                }
            </div>
        }</div>
    );
};
export default Question;
